const toAuditDateTimestamp = (value) => {
  const text = String(value || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) return Number.POSITIVE_INFINITY;
  const timestamp = Date.parse(`${text}T00:00:00Z`);
  return Number.isFinite(timestamp) ? timestamp : Number.POSITIVE_INFINITY;
};

// Canonical certification-cycle stage order. Used as a tiebreaker so cycles
// with equal or missing Audit Dates still follow the correct sequence
// (Initial → Surveillance → Re-certification) and an Initial can never appear
// after a Surveillance/Re-certification. Audit Date remains the primary sort.
export const auditCycleStageRank = (cycle) => {
  const text = String(
    cycle?.serviceItemName || cycle?.serviceItem || cycle?.stageLabel || cycle?.stageName || cycle?.label || ''
  ).toLowerCase();
  if (
    text.includes('initial')
    || /stage\s*1/.test(text)
    // ISCC labels its first cycle simply as "Certification" (for example,
    // "Certification #Indonesia"), rather than "Initial Certification".
    // Without this it sorts after Surveillance on same-day cycles, so the
    // workflow opens the wrong scope and leaves Certification locked.
    || (text.includes('certification') && !text.includes('recert'))
  ) return 0;
  if (/stage\s*2/.test(text)) return 1;
  if (text.includes('surveillance')) {
    if (/2|ii\b|2nd|second/.test(text)) return 3;
    return 2; // surveillance (1st / generic)
  }
  if (text.includes('recert') || text.includes('re-cert') || text.includes('recertification')) return 4;
  return 5; // unknown stages sort last within the same date
};

// Which Audit Summary Report PPTX template a cycle maps to. Reuses the same
// label-matching signals as auditCycleStageRank, but collapses Surveillance
// 1st/2nd into one 'surveillance' bucket (there is only one Surveillance
// template, not one per occurrence) and treats an explicit `:s1`/`:s2` key
// suffix (Initial Certification's own convention) as authoritative when present.
export const resolveAuditSummaryStageKey = (cycle) => {
  const key = String(cycle?.key || '');
  if (key.endsWith(':s1')) return 'stage1';
  if (key.endsWith(':s2')) return 'stage2';

  const text = String(
    cycle?.serviceItemName || cycle?.serviceItem || cycle?.stageLabel || cycle?.stageName || cycle?.label || ''
  ).toLowerCase();
  if (text.includes('recert') || text.includes('re-cert')) return 'recertification';
  if (text.includes('surveillance')) return 'surveillance';
  if (/stage\s*2/.test(text)) return 'stage2';
  if (text.includes('initial') || /stage\s*1/.test(text)) return 'stage1';
  return null;
};

export const sortAuditCyclesByDate = (cycles = []) => cycles
  .map((cycle, originalIndex) => ({ cycle, originalIndex }))
  .sort((left, right) => {
    const dateDifference = toAuditDateTimestamp(left.cycle?.auditDate) - toAuditDateTimestamp(right.cycle?.auditDate);
    if (dateDifference) return dateDifference;
    const rankDifference = auditCycleStageRank(left.cycle) - auditCycleStageRank(right.cycle);
    if (rankDifference) return rankDifference;
    return left.originalIndex - right.originalIndex;
  })
  .map(({ cycle }) => cycle);

// Resolves which cycle should be the "active" one on the Audit Process page (the one
// that gets the workflow stepper, the expanded accordion, and drives Scheduling's
// default scope). Prefers the first NOT-yet-fully-completed cycle in date order (each
// stage's own `isFullyCompleted` is the caller's isWorkflowFullyCompleted(workflowProgress)
// result). Completion drives activation, not literal calendar date -- a cycle whose
// Certificate has already been issued must hand off to the next cycle immediately, not
// wait for its own Audit Date to pass. Once every cycle is fully completed, falls back
// to the LAST cycle so the page still has a "current" cycle to display.
// Identity of the independent audit "track" a cycle belongs to.
//
// A project can carry several audit tracks that run at their own pace:
//   * Single Audit, multi standard  -> ONE TRACK PER STANDARD. Each standard is
//     its own audit and must progress independently; ISO 9001 reaching Technical
//     Review must never stop ISO 14001 working its own Audit cycle.
//   * Integrated / Combined         -> ONE TRACK FOR THE WHOLE GROUP. Every member
//     standard is audited together in a single process, so the group's cycles form
//     one sequence and only one of them can be active.
//
// Stages that carry no standard identity at all (the projectBasedStages /
// fallbackTaskStages sources, used for legacy projects) fall back to a single
// shared track, preserving the previous project-global behaviour for them.
export const resolveCycleTrackKey = (stage) => {
  if (!stage) return 'default';
  if (stage.isMethodologyGroup || String(stage.key || '').startsWith('mg-')) return 'methodology-group';
  const standard = stage.standardId ?? stage.standardName ?? null;
  if (standard == null || String(standard).trim() === '') return 'default';
  return `${stage.projectServiceId ?? ''}::${standard}`;
};

// The active cycle of EVERY track, not just the project's first one.
//
// resolveCurrentCycleKey answers "which single cycle drives the page header" and
// is still used for that. This answers the different, per-track question the
// cycle cards and the document scope selector actually need: a scope belonging to
// another standard's independent audit must stay reachable regardless of how far
// any other standard has progressed.
export const resolveActiveCycleKeys = (stages = []) => {
  if (!Array.isArray(stages) || stages.length === 0) return [];
  const byTrack = new Map();
  stages.forEach((stage) => {
    const track = resolveCycleTrackKey(stage);
    if (!byTrack.has(track)) byTrack.set(track, []);
    byTrack.get(track).push(stage);
  });
  const keys = [];
  byTrack.forEach((trackStages) => {
    const key = resolveCurrentCycleKey(trackStages);
    if (key != null && !keys.includes(key)) keys.push(key);
  });
  return keys;
};

export const resolveCurrentCycleKey = (stages = []) => {
  if (!Array.isArray(stages) || stages.length === 0) return null;
  // A newly activated cycle can have open entry tasks while a stale/legacy
  // completion signal still marks its card as fully completed. Open scoped
  // workflow work is authoritative for activation, so prefer that cycle before
  // falling back to the computed completion flag.
  const cycleWithOpenTasks = stages.find((stage) => stage?.hasOpenWorkflowTasks);
  if (cycleWithOpenTasks?.key) return cycleWithOpenTasks.key;
  const nextActive = stages.find((stage) => !stage?.isFullyCompleted);
  return (nextActive || stages[stages.length - 1])?.key || null;
};

// Decides how one cycle is presented on the Audit Process page, given which cycle
// resolveCurrentCycleKey picked as active. Returns only the fields to override, so
// callers spread it over the stage.
//
//   active cycle    -> shows the workflow stepper, keeps its own computed badge
//   completed cycle -> keeps its own "Audit Completed" badge, stepper collapsed
//                      (isWorkflowFullyCompleted's contract: swap the full stepper
//                       for the compact one-line completed summary)
//   anything else   -> "Upcoming Audit Cycle"
//
// Regression: every non-active cycle used to be relabelled "Upcoming Audit Cycle",
// so a cycle that had already finished read as still to come. Most visible on a full
// 3-year program -- e.g. 2025 Surveillance 1st + 2nd complete and the 2026
// Recertification active: both finished 2025 cycles showed as upcoming.
// `done` is checked alongside `isFullyCompleted` on purpose: only the scope-driven
// stage source sets `isFullyCompleted`, while projectBasedStages/fallbackTaskStages
// carry completion as `done` only -- without it, a finished cycle from those two
// sources would still be relabelled "Upcoming Audit Cycle".
// `currentStageKeys` accepts either one key (the original single-track contract)
// or a list/Set of the per-track active keys from resolveActiveCycleKeys, so a
// multi-standard Single Audit shows each standard's own running cycle.
const isActiveCycleKey = (stageKey, currentStageKeys) => {
  if (stageKey == null || currentStageKeys == null) return false;
  if (currentStageKeys instanceof Set) return currentStageKeys.has(stageKey);
  if (Array.isArray(currentStageKeys)) return currentStageKeys.includes(stageKey);
  return currentStageKeys === stageKey;
};

export const resolveCycleDisplayState = (stage, currentStageKey) => {
  // The active cycle wins over its own completion flag. resolveCurrentCycleKey can
  // legitimately point at a completed cycle (every cycle done -> last one; or a
  // stale completion flag on a cycle that still has open tasks), and the page
  // resolves `currentAuditCycle` by finding the stage with showWorkflowStepper --
  // hiding it there would drop the sticky stepper/doc-tab scope back to the first
  // (usually oldest) cycle. renderAuditWorkflowStepper already swaps in the compact
  // completed summary on its own when the workflow is finished.
  if (isActiveCycleKey(stage?.key, currentStageKey)) {
    return { showWorkflowStepper: true };
  }
  if (stage?.isFullyCompleted || stage?.done) {
    return { showWorkflowStepper: false };
  }
  return {
    statusClass: 'badge badge-next-audit',
    statusLabel: 'Upcoming Audit Cycle',
    showWorkflowStepper: false,
  };
};

export const isSchedulingScopeConfirmed = (schedulingData) => {
  const rows = Array.isArray(schedulingData?.auditSchedules) ? schedulingData.auditSchedules : [];
  const assignedRows = rows.filter((row) => String(row?.employeeId || '').trim());
  return assignedRows.length > 0
    && assignedRows.every((row) => String(row?.scheduleStatus || '').trim().toLowerCase() === 'confirmed');
};

const numericOrder = (item) => {
  const value = item?.sortOrder ?? item?.sequence ?? item?.displayOrder ?? item?.orderIndex;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : Number.POSITIVE_INFINITY;
};

export const sortAuditProductCategories = (items = []) => items
  .map((item, originalIndex) => ({ item, originalIndex }))
  .sort((left, right) => {
    const leftOrder = numericOrder(left.item);
    const rightOrder = numericOrder(right.item);
    if (Number.isFinite(leftOrder) || Number.isFinite(rightOrder)) {
      if (!Number.isFinite(leftOrder)) return 1;
      if (!Number.isFinite(rightOrder)) return -1;
      if (leftOrder !== rightOrder) return leftOrder - rightOrder;
    }
    const dateDifference = toAuditDateTimestamp(left.item?.proposeDate) - toAuditDateTimestamp(right.item?.proposeDate);
    if (dateDifference !== 0) return dateDifference;
    return left.originalIndex - right.originalIndex;
  })
  .map(({ item }) => item);

export const dedupeScheduleSites = (sites = []) => {
  const seen = new Set();
  return sites.filter((site) => {
    const key = String(
      site?.sourceId
      || `${site?.siteName || site?.name || ''}::${site?.address || site?.location || ''}`
      || site?.id
    ).trim().toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};
